import React, {useEffect, useMemo, useState} from "react";
import moment from "moment";
import cogoToast from "cogo-toast";
import {landlordService} from "../../services/landlordService";

const TRIGGER_LABELS = {
    check: "Dry run",
    activation: "Activation",
    sync: "Last sync"
};

const STATUS_BADGES = {
    invalid: {className: "badge-danger", label: "Fails"},
    warnings: {className: "badge-warning", label: "Warnings"},
    valid: {className: "badge-success", label: "Passes"}
};

// efeito de cada listing na ultima sincronizacao (so existe nos relatorios das sincronizacoes)
const EFFECT_LABELS = {
    published: "Published",
    unpublished: "Unpublished",
    not_published: "Not published",
    import_failed: "Import failed, previous version kept"
};

const countByField = (listings, key) => {
    let counts = {};
    listings.forEach(listing => (listing[key] || []).forEach(issue => {
        counts[issue.field] = (counts[issue.field] || 0) + 1;
    }));
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
};

// texto simples com os listings que falham, para enviar ao fornecedor
const buildIssuesText = (report) => {
    return report.listings.filter(listing => listing.status === "invalid").map(listing => {
        let header = `${listing.name}${listing.id ? ` (${listing.id})` : ""}`;
        return [header, ...listing.errors.map(error => `  - ${error.field}: ${error.message}`)].join("\n");
    }).join("\n\n");
};

const IssueList = ({issues, className}) => (
    <ul className={"mb-1 pl-3 " + className} style={{fontSize: "0.8rem"}}>
        {issues.map((issue, idx) => <li key={idx}><strong>{issue.field}</strong>: {issue.message}</li>)}
    </ul>
);

const ReportRow = ({listing}) => {
    const [showWarnings, setShowWarnings] = useState(false);
    let badge = STATUS_BADGES[listing.status] || STATUS_BADGES.invalid;
    return (
        <tr>
            <td style={{minWidth: 200}}>
                <div>{listing.name}</div>
                {listing.address && listing.address !== listing.name && <small className="d-block text-muted">{listing.address}</small>}
                {listing.id && <small className="d-block text-muted">{listing.id}</small>}
            </td>
            <td>{listing.accommodation || "-"}</td>
            <td>
                <span className={"badge " + badge.className}>{badge.label}</span>
                {listing.effect && <small className="d-block text-muted mt-1">{EFFECT_LABELS[listing.effect] || listing.effect}</small>}
            </td>
            <td style={{whiteSpace: "normal"}}>
                {listing.errors.length > 0 && <IssueList issues={listing.errors} className="text-danger"/>}
                {listing.warnings.length > 0 && <>
                    <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setShowWarnings(!showWarnings)}>
                        {showWarnings ? "Hide" : "Show"} {listing.warnings.length} warning{listing.warnings.length === 1 ? "" : "s"}
                    </button>
                    {showWarnings && <IssueList issues={listing.warnings} className="text-muted"/>}
                </>}
                {listing.errors.length === 0 && listing.warnings.length === 0 && <small className="text-muted">No issues</small>}
            </td>
        </tr>
    );
};

const HybridIntegrationReport = ({report}) => {
    const [filter, setFilter] = useState(report?.invalid > 0 ? "invalid" : "all");
    const errorFields = useMemo(() => countByField(report?.listings || [], "errors"), [report]);
    const warningFields = useMemo(() => countByField(report?.listings || [], "warnings"), [report]);

    useEffect(() => {
        setFilter(report?.invalid > 0 ? "invalid" : "all");
    }, [report]);

    if (!report) return null;
    // um relatorio so com o erro do feed (primeira sincronizacao falhada) nao tem contagens
    report = {total: 0, valid: 0, invalid: 0, withWarnings: 0, ...report};
    let listings = report.listings || [];
    let shown = filter === "all" ? listings : listings.filter(listing => listing.status === filter);
    let isSync = report.trigger === "sync";

    const copyIssues = async () => {
        try {
            await navigator.clipboard.writeText(buildIssuesText(report));
            cogoToast.success("Failing listings copied to the clipboard.", {hideAfter: 4, position: 'top-center'});
        } catch (e) {
            cogoToast.error("Couldn't copy to the clipboard.", {hideAfter: 4, position: 'top-center'});
        }
    };

    return (
        <div className="mt-3">
            <p className="mb-2">
                <strong>{TRIGGER_LABELS[report.trigger] || "Report"}</strong>
                {report.date && <> on {moment(report.date).format("DD/MM/YYYY HH:mm")}</>}
                {report.integration_url && <small className="d-block text-muted text-break">{report.integration_url}</small>}
            </p>
            {report.feedError && <div className="alert alert-danger py-2">
                The last sync couldn't read the feed{report.feedErrorDate ? ` on ${moment(report.feedErrorDate).format("DD/MM/YYYY HH:mm")}` : ""}: {report.feedError}.
                Listings were left as they were.
            </div>}
            <div className="d-flex flex-wrap align-items-center mb-2">
                <span className="mr-3"><strong>{report.total}</strong> listings</span>
                <span className="mr-3 text-success"><strong>{report.valid}</strong> {isSync ? "published" : "pass"}</span>
                <span className="mr-3 text-danger"><strong>{report.invalid}</strong> {isSync ? "not published" : "fail"}</span>
                <span className="mr-3 text-warning"><strong>{report.withWarnings}</strong> pass with warnings</span>
                {isSync && report.removedFromFeed > 0 && <span className="mr-3 text-muted"><strong>{report.removedFromFeed}</strong> removed from the feed</span>}
            </div>
            {errorFields.length > 0 && <p className="mb-1" style={{fontSize: "0.8rem"}}>
                <span className="text-danger">Most common errors:</span> {errorFields.slice(0, 8).map(([field, count]) => `${field} (${count})`).join(", ")}
            </p>}
            {warningFields.length > 0 && <p className="mb-2" style={{fontSize: "0.8rem"}}>
                <span className="text-warning">Most common warnings:</span> {warningFields.slice(0, 8).map(([field, count]) => `${field} (${count})`).join(", ")}
            </p>}
            <div className="d-flex flex-wrap align-items-center mb-2">
                {[["all", `All (${listings.length})`], ["invalid", `Failing (${report.invalid})`],
                    ["warnings", `Warnings (${report.withWarnings})`], ["valid", `Passing (${report.valid - report.withWarnings})`]].map(([value, label]) => (
                    <button key={value} type="button" onClick={() => setFilter(value)}
                            className={"btn btn-sm mr-2 mb-1 " + (filter === value ? "btn-primary" : "btn-outline-primary")}>
                        {label}
                    </button>
                ))}
                {report.invalid > 0 && <button type="button" className="btn btn-sm btn-outline-secondary mb-1 ml-auto" onClick={copyIssues}>
                    Copy failing listings
                </button>}
            </div>
            <div style={{maxHeight: "50vh", overflowY: "auto"}}>
                <table className="table table-sm">
                    <thead>
                    <tr>
                        <th>Listing</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Issues</th>
                    </tr>
                    </thead>
                    <tbody>
                    {shown.map((listing, idx) => <ReportRow key={listing.key || idx} listing={listing}/>)}
                    {shown.length === 0 && <tr><td colSpan={4} className="text-muted">No listings in this filter.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// relatorio guardado da ultima sincronizacao, com opcao de voltar a validar o feed (sem publicar nada)
export const HybridIntegrationReportPanel = ({landlordId, errorMessages = {}}) => {
    const [report, setReport] = useState(undefined);
    const [loading, setLoading] = useState(false);

    const run = async (action) => {
        setLoading(true);
        try {
            setReport((await action()) || null);
        } catch (e) {
            let message = e.error?.message || e.validationErrors?.[0]?.msg || e.message;
            cogoToast.error(errorMessages[message] || message || "Something went wrong", {hideAfter: 6, position: 'top-center', heading: 'Error'});
        }
        setLoading(false);
    };

    useEffect(() => {
        run(() => landlordService.getHybridIntegrationReport(landlordId));
    }, [landlordId]);

    return (
        <div>
            <div className="d-flex align-items-center">
                <button type="button" className="btn btn-outline-primary btn-sm" disabled={loading}
                        onClick={() => run(() => landlordService.checkHybridIntegration(landlordId))}>
                    {loading ? "Checking..." : "Re-check feed now"}
                </button>
                <small className="text-muted ml-3">A re-check only validates the feed. Nothing is published or unpublished until the next hourly sync.</small>
            </div>
            {report === null && !loading && <p className="mt-3 text-muted">No report yet. It's saved on activation and after every hourly sync.</p>}
            {report && <HybridIntegrationReport report={report}/>}
        </div>
    );
};

export default HybridIntegrationReport;
